import { DEFAULTS } from 'components/Login/constants';
import {
  Content,
  Divider,
  Input,
  Spacer,
  VerifyGroup
} from 'components/Login/styles';
import useToast from 'hooks/useToast';
import Loading from 'layouts/Loading';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import AuthAPI from 'services/apis/AuthAPI';
import UserActions from 'store/stores/User/actions';
import Captcha from 'uikit/Captcha';
import Formatter from 'utils/Formatter';

const LoginModule = ({ isPerson }) => {
  const { t } = useTranslation('login');
  const dispatch = useDispatch();
  const [data, setData] = useState(DEFAULTS.data);
  const [pin, setPin] = useState('');
  const [validation, setValidation] = useState(DEFAULTS.validation);
  const [isVerifyLoading, setIsVerifyLoading] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [maxLength, setMaxLength] = useState(11);
  const [isPassword, setIsPassword] = useState(false);
  const [step, setStep] = useState(0);

  const [second, setSecond] = useState('00');
  const [minute, setMinute] = useState('02');
  const [isTimer, setIsTimer] = useState(false);
  const [counter, setCounter] = useState(120);

  const localAuthAPI = new AuthAPI();
  const toast = useToast();

  useEffect(() => {
    setIsVerifyLoading(false);
    setIsSubmitLoading(false);
    setData(DEFAULTS.data);
    setPin('');
    setValidation(DEFAULTS.validation);
    setStep(0);
    setIsPassword(false);
  }, [isPerson]);

  useEffect(() => {
    let intervalId;

    if (isTimer && counter > 0) {
      intervalId = setInterval(() => {
        const secondCounter = counter % 60;
        const minuteCounter = Math.floor(counter / 60);

        const computedSecond =
          String(secondCounter).length === 1
            ? `0${secondCounter}`
            : secondCounter;
        const computedMinute =
          String(minuteCounter).length === 1
            ? `0${minuteCounter}`
            : minuteCounter;

        setSecond(computedSecond);
        setMinute(computedMinute);

        setCounter(counter => counter - 1);
      }, 1000);
    } else {
      if (counter === 0) {
        setStep(0);
      }
      setCounter(120);
    }

    return () => clearInterval(intervalId);
  }, [isTimer, counter]);

  useEffect(() => {
    if (step === 0) {
      setSecond('00');
      setMinute('02');
      setIsTimer(false);
      setCounter(120);
    } else {
      if (!isTimer) setIsTimer(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const onHandleNationalCode = event => {
    const _code = Formatter.changeNumberToEn(event.target.value).replace(
      /\D/,
      ''
    );
    if (_code.length === 10) {
      setValidation({
        ...validation,
        national_code: !Formatter.isNationalCode(_code)[1]
      });
    } else {
      validation.national_code !== undefined &&
        setValidation({ ...validation, national_code: undefined });
    }
    setData({ ...data, national_code: _code });
  };

  const onHandleMobile = event => {
    const _mobile = Formatter.changeNumberToEn(event.target.value).replace(
      /\D/,
      ''
    );
    _mobile.substr(0, 1) === '0' ? setMaxLength(11) : setMaxLength(10);

    if (_mobile.length === maxLength) {
      setValidation({
        ...validation,
        mobile: !Formatter.isMobileNumber(_mobile)[1]
      });
    } else {
      validation.mobile !== undefined &&
        setValidation({ ...validation, mobile: undefined });
    }
    setData({ ...data, mobile: _mobile });
  };

  const onHandleCompanyType = event => {
    const _type = event.target.value;
    setData({ ...data, company_type: _type });
  };

  const onHandlePassword = event => {
    const _password = event.target.value;
    if (_password.length === 0) {
      setValidation({ ...validation, password: undefined });
    } else {
      if (_password.length > 7) {
        setValidation({ ...validation, password: true });
      } else {
        validation.password !== false &&
          setValidation({ ...validation, password: false });
      }
    }

    setData({ ...data, password: _password });
  };

  const onHandleCaptcha = _captcha => {
    if (step === 0) {
      setData({ ...data, captcha: _captcha });
    }
  };

  const onHandlePin = event => {
    const _pin = Formatter.changeNumberToEn(event.target.value).replace(
      /\D/,
      ''
    );
    if (_pin.length !== 5) {
      setValidation({ ...validation, pin: false });
    } else {
      setValidation({ ...validation, pin: true });
    }

    setPin(_pin);
  };

  const onLoginStep1 = () => {
    setIsVerifyLoading(true);
    const _body = {
      captcha: data.captcha,
      national_code: data.national_code,
      mobile: maxLength === 10 ? data.mobile : data.mobile.substr(1, 10),
      account_type: isPerson ? 'Natural' : 'Legal',
      company_type: isPerson ? 'None' : data.company_type
    };

    localAuthAPI.loginStep1(_body).then(response => {
      if (response && response.code === 200) {
        setTimeout(() => {
          toast(t('notification.step1.success'), 'success');
          setStep(1);
          setIsVerifyLoading(false);
        }, 1000);
      } else {
        toast(response.msg || t('notification.step1.error'), 'error');
        setTimeout(() => {
          setIsVerifyLoading(false);
        }, 1000);
      }
    });
  };

  const onLoginStep2 = () => {
    setIsSubmitLoading(true);
    const _body = {
      national_code: data.national_code,
      mobile: maxLength === 10 ? data.mobile : data.mobile.substr(1, 10),
      account_type: isPerson ? 'Natural' : 'Legal',
      company_type: isPerson ? 'None' : data.company_type,
      pin: pin
    };

    localAuthAPI.loginStep2(_body).then(response => {
      if (response && response.jwt) {
        dispatch(UserActions.SetToken(response.jwt));
        dispatch(UserActions.SetIsLogin(true));
        toast(t('notification.step2.success'), 'success');
      } else {
        toast(response.msg || t('notification.step2.error'), 'error');
        setTimeout(() => {
          setIsSubmitLoading(false);
        }, 1000);
      }
    });
  };

  const [stats, setStats] = useState({});

  const onGetStats = () => {
    localAuthAPI
      .infoOne()
      .then(response => {
        if (response) {
          setStats(response);
        }
      })
      .catch(error => {
        console.error('Error fetching stats:', error);
      });
  };

  useEffect(() => {
    onGetStats();
  }, []);
  return (
    <Content>
      <Input
        className={
          validation.national_code === undefined
            ? ''
            : validation.national_code
            ? 'valid'
            : 'invalid'
        }
      >
        <input
          className={'pw-number'}
          value={data.national_code}
          disabled={step !== 0}
          placeholder={
            isPerson
              ? 'کد ملی خود را وارد کنید...'
              : 'کد ملی مدیرعامل را وارد کنید...'
          }
          maxLength={10}
          inputMode={'numeric'}
          onChange={onHandleNationalCode}
        />
      </Input>
      <Spacer />
      <Input
        className={
          validation.mobile === undefined
            ? ''
            : validation.mobile
            ? 'valid'
            : 'invalid'
        }
      >
        <input
          className={'pw-number'}
          value={data.mobile}
          disabled={step !== 0}
          placeholder={
            isPerson
              ? 'شماره موبایل به نام خود را وارد کنید...'
              : 'شماره موبایل به نام مدیرعامل را وارد کنید...'
          }
          maxLength={maxLength}
          inputMode={'numeric'}
          onChange={onHandleMobile}
        />
      </Input>
      {!isPerson && (
        <>
          <Spacer />
          <Input className={'select'}>
            <select
              className={`${data.company_type === '' ? 'placeholder' : ''} `}
              value={data.company_type}
              onChange={onHandleCompanyType}
              disabled={step !== 0}
            >
              <option value={''} disabled={true} className={'default'}>
                نوع شرکت را انتخاب کنید...
              </option>
              <option value={'PrivateHeld'}>سهامی خاص</option>
              <option value={'Cooperative'}>تعاونی</option>
            </select>
          </Input>
        </>
      )}
      <Spacer size={25} />
      <VerifyGroup>
        {step === 0 ? (
          <button
            disabled={
              data.captcha.length === 0 ||
              !validation.national_code ||
              !validation.mobile
            }
            onClick={onLoginStep1}
          >
            {isVerifyLoading ? (
              <Loading color={'#fff'} />
            ) : (
              'ارسال کد تأیید به موبایل'
            )}
          </button>
        ) : (
          <Input className={'pin'}>
            <input
              className={'pw-number'}
              value={pin}
              disabled={step !== 1}
              placeholder={'کد تأیید را وارد کنید...'}
              maxLength={5}
              inputMode={'numeric'}
              onChange={onHandlePin}
            />
            <div className={'timer'}>{`${minute} : ${second}`}</div>
            {isVerifyLoading && <Loading isRelative={false} />}
          </Input>
        )}

        <Captcha
          isActive={data.captcha.length > 0}
          isDisabled={step !== 0}
          onCaptcha={_captcha => onHandleCaptcha(_captcha)}
          captcha={data.captcha}
        />
      </VerifyGroup>

      {step === 1 && false && (
        <>
          <Spacer />
          <Input className={'password'}>
            <input
              value={data.password}
              disabled={false}
              placeholder={'رمز عبور'}
              type={!isPassword ? 'password' : 'text'}
              onChange={onHandlePassword}
              autoComplete={'new-password'}
            />
            <svg
              className={`icon ${isPassword ? 'on' : ''}`}
              viewBox="0 0 31 23"
              onClick={() => setIsPassword(!isPassword)}
            >
              <path d="M4.82199 15.828C5.4083 16.2372 5.99958 16.6139 6.59346 16.9583L0.384331 21.5616L1.47134 23L8.36895 17.8865C10.7681 19.0047 13.1848 19.5857 15.5 19.5857C18.9651 19.5857 22.6575 18.2862 26.1782 15.828C28.8116 13.9892 30.4184 12.1651 30.4856 12.0883L31 11.5L30.4856 10.9117C30.4184 10.8351 28.8116 9.01108 26.1782 7.17227C25.5806 6.7548 24.978 6.37176 24.3725 6.02199L30.5551 1.43844L29.4681 0L22.5939 5.09638C20.207 3.9892 17.8034 3.41453 15.5 3.41453C12.0349 3.41453 8.34246 4.71381 4.82199 7.17227C2.18844 9.01108 0.581581 10.8351 0.514412 10.9117L0 11.5L0.514412 12.0883C0.581581 12.1649 2.18844 13.9892 4.82199 15.828ZM9.15109 11.5C9.15109 8.03252 11.9992 5.21117 15.5 5.21117C15.5598 5.21117 15.6194 5.21305 15.679 5.21398C17.3507 5.2599 18.863 5.94937 19.9717 7.04038L18.4975 8.13326C17.6977 7.43372 16.6485 7.00805 15.5 7.00805C12.9996 7.00805 10.9651 9.02303 10.9651 11.5C10.9651 12.1792 11.1189 12.8229 11.3925 13.4007L9.91786 14.4938C9.42899 13.6031 9.15109 12.583 9.15109 11.5ZM18.2211 11.5C18.2211 12.9862 17.0004 14.1953 15.5 14.1953C14.9352 14.1953 14.4099 14.0236 13.9745 13.7307L18.0912 10.6786C18.1752 10.9377 18.2211 11.2137 18.2211 11.5ZM12.7792 11.5C12.7792 10.014 13.9998 8.80492 15.5 8.80492C16.0511 8.80492 16.5641 8.96844 16.9931 9.24864L12.8972 12.285C12.8206 12.0365 12.7792 11.7729 12.7792 11.5ZM12.4745 14.8426C13.2777 15.5567 14.3383 15.992 15.5 15.992C18.0006 15.992 20.0349 13.977 20.0349 11.5C20.0349 10.808 19.8759 10.152 19.5924 9.56584L21.0644 8.4746C21.5641 9.3728 21.8489 10.4038 21.8489 11.5C21.8489 14.9677 19.0008 17.7888 15.5 17.7888C13.7498 17.7888 12.1564 17.0825 11.0023 15.9341L12.4745 14.8426ZM25.0955 8.61446C26.6875 9.72187 27.8937 10.8412 28.5523 11.4998C27.5434 12.5067 25.245 14.5962 22.2157 16.0903C23.1277 14.7854 23.6629 13.2036 23.6629 11.5C23.6629 10.0004 23.2481 8.59525 22.527 7.39015L22.7829 7.20062C23.6497 7.6605 24.4285 8.1506 25.0955 8.61446ZM8.78426 6.90965C7.87228 8.21455 7.33705 9.79637 7.33705 11.5C7.33705 12.9867 7.7448 14.3806 8.45433 15.5787L8.18187 15.7806C7.32925 15.3264 6.56248 14.8431 5.9045 14.3855C4.31255 13.2781 3.1061 12.1585 2.44742 11.5002C3.45661 10.4933 5.75526 8.40385 8.78426 6.90965Z" />
            </svg>
          </Input>
          <Spacer />
          <Divider />
        </>
      )}

      <Spacer size={40} />
      <div className={'pw-actions'}>
        <button
          className={'pw-login'}
          disabled={
            !(validation.national_code && validation.mobile && validation.pin)
          }
          onClick={() => {
            if (validation.national_code && validation.mobile && validation.pin)
              onLoginStep2();
          }}
        >
          {isSubmitLoading ? <Loading color={'#fff'} /> : 'ورود'}
        </button>
        {stats && stats.lastDays ? (
          <Link to={'/register/'} className={'pw-register'}>
            ثبت‌نام
          </Link>
        ) : (
          ''
        )}
        {stats && stats.lastDays ? (
          <div className={'pw-text'}>
            قبلا ثبت‌نام نکرده‌اید؟
            <Link to={'/register/'}>ثبت‌نام کنید</Link>
          </div>
        ) : (
          ''
        )}
      </div>
    </Content>
  );
};

export default LoginModule;
